import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { getChatChannelName } from '../../../../utils/getChatChannelName';
import { FORWARD_MESSAGE_CHANNEL_LIMIT } from '../../ForwardMessageProvider';
import { useCurrentUser } from '../../../../auth/currentUserContext';
import { Avatar, AvatarImage } from '../../../../components/ui/avatar';
import { Card } from '../../../../components/ui/card';
import { cn } from '../../../../lib/utils';

type ForwardChannelCardProps = {
  channel: GroupChannel;
  selectedChannelsUrls: string[];
  isPending: boolean;
  onClick: () => void;
  disabled?: boolean;
};

const ForwardChannelCard: React.FC<ForwardChannelCardProps> = ({
  channel,
  selectedChannelsUrls,
  isPending,
  onClick,
  disabled,
}) => {
  const currentUser = useCurrentUser();
  const channelName = useMemo(
    () => getChatChannelName(channel, currentUser),
    [channel, currentUser]
  );
  const isSelected = useMemo(
    () => selectedChannelsUrls.includes(channel.url),
    [channel, selectedChannelsUrls]
  );
  const shouldDisableCard =
    selectedChannelsUrls.length >= FORWARD_MESSAGE_CHANNEL_LIMIT && !isSelected;

  return (
    <Card className="relative rounded-none p-0 shadow-none">
      <button
        type="button"
        onClick={onClick}
        disabled={isPending || shouldDisableCard || disabled}
        aria-pressed={isSelected}
        className="flex h-16 w-full items-center gap-4 p-3 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Avatar>
          <AvatarImage alt="" src={channel.coverUrl} />
        </Avatar>
        <span className="min-w-0 flex-1 truncate text-sm">{channelName}</span>
        <span
          aria-hidden="true"
          className={cn(
            'flex size-4 shrink-0 items-center justify-center rounded-[4px] border text-primary-foreground',
            isSelected ? 'border-primary bg-primary' : 'border-input'
          )}
        >
          {isSelected && <Check className="size-3.5" />}
        </span>
      </button>
    </Card>
  );
};

export default ForwardChannelCard;
